import { useState } from 'react';
import { Lock } from 'lucide-react';
import cn from '../../utils/cn';
import { formatBytes, formatListDate } from '../../utils/format';
import FileTypeIcon, { FolderGlyph } from './FileTypeIcon';
import DocumentActions from './DocumentActions';
import Thumbnail from './Thumbnail';
import { Checkbox } from '../common/Field';

/** Folder tile (v2): 28px accent folder glyph, name, "n files". */
function FolderTile({ item, selected, onOpen, actions }) {
  return (
    <div
      className={cn('panel folder-tile', selected && 'is-selected')}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(item))}
    >
      <FolderGlyph size={28} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="title truncate" style={{ fontWeight: 600 }}>
          {item.name}
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {item.itemCount} {item.itemCount === 1 ? 'item' : 'items'}
        </div>
      </div>
      <span onClick={(e) => e.stopPropagation()}>
        <DocumentActions item={item} actions={actions} />
      </span>
    </div>
  );
}

/** Document card (v2): badge + "v4 · 310 KB", name, 2-line summary, owner · date, Share. */
function DocCard({ item, selection, onOpen, actions }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const selected = selection?.isSelected(item.id);
  const summary =
    item.description ||
    (item.tags?.length ? `Tagged ${item.tags.join(', ')}` : `${item.department?.name || ''} document${item.path ? ` in ${item.path}` : ''}.`);

  return (
    <div
      className={cn('panel doc-card', selected && 'is-selected', menuOpen && 'is-menu-open')}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(item))}
    >
      {selection && (
        <span className="doc-card__check" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={Boolean(selected)} onChange={() => selection.toggle(item.id)} aria-label={`Select ${item.name}`} />
        </span>
      )}
      <div className="doc-card__top">
        <FileTypeIcon name={item.name} size="lg" />
        <span className="muted" style={{ fontSize: 12 }}>
          v{item.currentVersionNo} · {formatBytes(item.sizeBytes)}
        </span>
      </div>
      <Thumbnail item={item} />
      <div className="doc-card__name">
        {item.name}
        {item.isConfidential && (
          <span className="pill pill-warn" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
            <Lock size={10} /> Confidential
          </span>
        )}
      </div>
      <div className="doc-card__summary clamp-2">{summary}</div>
      <div className="doc-card__foot">
        <span className="muted truncate" style={{ fontSize: 12 }}>
          {item.owner?.name} · {formatListDate(item.updatedAt)}
        </span>
        <span className="row" style={{ gap: 2 }} onClick={(e) => e.stopPropagation()}>
          {item.capabilities?.includes('share') && (
            <button type="button" className="link" onClick={() => actions.share(item)}>
              Share
            </button>
          )}
          <DocumentActions item={item} actions={actions} onOpenChange={setMenuOpen} />
        </span>
      </div>
    </div>
  );
}

export default function DocumentGrid({ items, selection, onOpen, actions }) {
  const folders = items.filter((i) => i.type === 'folder');
  const docs = items.filter((i) => i.type !== 'folder');

  return (
    <div className="stack" style={{ gap: 16 }}>
      {folders.length > 0 && (
        <div className="grid-folders">
          {folders.map((item) => (
            <FolderTile key={item.id} item={item} selected={selection?.isSelected(item.id)} onOpen={onOpen} actions={actions} />
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div className="grid-cards">
          {docs.map((item) => (
            <DocCard key={item.id} item={item} selection={selection} onOpen={onOpen} actions={actions} />
          ))}
        </div>
      )}
    </div>
  );
}

export { FolderTile };
